import { apiFetch } from './client'

export interface AuthUser {
  id: string
  email: string
}

export interface VerifyOTPResponse {
  token: string
  user: AuthUser
}

/**
 * Request a one-time code be sent to the given email.
 * The backend always returns 204 regardless of whether the email exists
 * (no enumeration). Let network/server errors propagate to the caller.
 */
export async function requestOTP(email: string): Promise<void> {
  await apiFetch<void>('/auth/request-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

/**
 * Exchange email + OTP code for a session token.
 * Uses skipAuthRedirect=true so that a wrong code (→ 401) doesn't bounce
 * the user away from /login before they can see the "Invalid code" error.
 */
export async function verifyOTP(email: string, code: string): Promise<VerifyOTPResponse> {
  return apiFetch<VerifyOTPResponse>('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
    skipAuthRedirect: true,
  })
}

/**
 * Invalidate the session server-side. Failures are intentionally swallowed —
 * the caller clears local state regardless.
 */
export async function logoutAPI(): Promise<void> {
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST' })
  } catch {
    // Best-effort: local session will be cleared by the caller either way.
  }
}

/** Fetch the currently authenticated user. */
export async function fetchMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>('/auth/me')
}
